import { memo, useMemo } from 'react'
import { twMerge } from 'tailwind-merge'
import type { SubagentState, ToolCall } from '../../../../types'
import { Collapsible } from '@highlight/ui'
import { BUILTIN_TOOLS } from '../../../../integrations/utils'
import { AskUserQuestionCard, parseAskUserQuestionInput } from '../../../../integrations/ask-user-question'
import { AskUserFeedbackCard, parseAskUserFeedbackInput } from '../../../../integrations/ask-user-feedback'
import { rendersInlineAskUserCard } from '../../../../integrations/ask-user-inline'
import { resolveToolView } from './tool-view'

const ToolCallIcon = memo(function ToolCallIcon({
  toolCall,
  isNewlyCompleted,
}: {
  toolCall: ToolCall
  isNewlyCompleted?: boolean
}) {
  const view = resolveToolView(toolCall)
  const FallbackIcon = view.icon

  return (
    <span className={`shrink-0 ${isNewlyCompleted ? 'animate-tool-icon-arrive' : ''}`}>
      {view.iconUrl ? (
        <img src={view.iconUrl} alt="" className="size-3.5 rounded object-contain dark:invert" />
      ) : (
        <FallbackIcon size={14} className="text-foreground-secondary" strokeWidth={2.25} />
      )}
    </span>
  )
})

// ---------------------------------------------------------------------------
// Formatted display helpers (matches agents branch style)
// ---------------------------------------------------------------------------

function formatDisplayValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'boolean' || typeof value === 'number') return String(value)
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

function KeyValuePairs({ data, maxEntries = 8 }: { data: Record<string, unknown>; maxEntries?: number }) {
  const entries = Object.entries(data).filter(([_, v]) => v !== null && v !== undefined)
  const visible = entries.slice(0, maxEntries)
  const remaining = entries.length - visible.length

  return (
    <div className="flex flex-col gap-1">
      {visible.map(([key, value]) => (
        <div key={key} className="type-prose-xs flex gap-2">
          <span className="text-foreground-placeholder shrink-0 font-mono text-[11px]">{key}</span>
          <span className="text-foreground-secondary min-w-0 break-words">{formatDisplayValue(value)}</span>
        </div>
      ))}
      {remaining > 0 && <span className="text-foreground-placeholder text-[11px]">+{remaining} more</span>}
    </div>
  )
}

function ResultDisplay({ result }: { result: unknown }) {
  const parsed = useMemo(() => {
    if (typeof result === 'string') {
      try {
        return JSON.parse(result)
      } catch {
        return null
      }
    }
    if (typeof result === 'object') return result
    return null
  }, [result])

  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    const important = ['success', 'message', 'error', 'data', 'results', 'count', 'reasoning']
    const importantEntries: Record<string, unknown> = {}
    const otherEntries: Record<string, unknown> = {}

    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (value === null || value === undefined) continue
      if (important.includes(key)) {
        importantEntries[key] = value
      } else {
        otherEntries[key] = value
      }
    }

    const entriesToShow = Object.keys(importantEntries).length > 0 ? importantEntries : otherEntries
    return <KeyValuePairs data={entriesToShow} />
  }

  const display = typeof result === 'string' ? result : String(result)
  return <span className="text-foreground-secondary type-prose-xs break-words">{display}</span>
}

function InputDisplay({ input }: { input: unknown }) {
  const parsed = useMemo(() => {
    if (typeof input === 'string') {
      try {
        return JSON.parse(input)
      } catch {
        return null
      }
    }
    if (typeof input === 'object' && input !== null) return input
    return null
  }, [input])

  if (parsed && typeof parsed === 'object') {
    const data = parsed as Record<string, unknown>
    if (Object.keys(data).length === 0) return null
    return <KeyValuePairs data={data} />
  }

  if (typeof input === 'string' && input.trim()) {
    const display = input
    return <span className="text-foreground-secondary type-prose-xs break-words">{display}</span>
  }

  return null
}

function ToolExpandedContent({ toolCall, description }: { toolCall: ToolCall; description: string | null }) {
  const hasInput = !!toolCall.toolInput
  const hasResult = !!toolCall.result
  const hasError = !!(toolCall as Record<string, unknown>).error

  return (
    <div className="ml-[3px] flex flex-col gap-2 border-l py-1 pl-3">
      {description && <span className="text-foreground-secondary type-prose-xs line-clamp-[12]">{description}</span>}

      {hasInput && (
        <div className="flex flex-col gap-1">
          <span className="text-foreground-placeholder text-[10px] uppercase tracking-wider">Input</span>
          <InputDisplay input={toolCall.toolInput} />
        </div>
      )}

      {hasError && (
        <div className="flex flex-col gap-1">
          <span className="text-red/50 text-[10px] uppercase tracking-wider">Error</span>
          <span className="text-red text-xs">{String((toolCall as Record<string, unknown>).error)}</span>
        </div>
      )}

      {hasResult && (
        <div className="flex flex-col gap-1">
          <span className="text-foreground-placeholder text-[10px] uppercase tracking-wider">Result</span>
          <ResultDisplay result={toolCall.result} />
        </div>
      )}
    </div>
  )
}

function SubagentListItem({ toolCall, justRevealed }: { toolCall: ToolCall; justRevealed?: boolean }) {
  const view = resolveToolView(toolCall)
  const FallbackIcon = view.icon

  return (
    <div className="flex min-w-0 items-center gap-1.5 py-1.5">
      <span className={twMerge('shrink-0', justRevealed ? 'animate-tool-icon-arrive' : '')}>
        {view.iconUrl ? (
          <img src={view.iconUrl} alt="" className="size-3.5 rounded object-contain dark:invert" />
        ) : (
          <FallbackIcon size={14} className="text-foreground-secondary" strokeWidth={2.25} />
        )}
      </span>
      <span className="text-foreground-secondary min-w-0 truncate text-left text-[12px] font-normal leading-4">
        {view.name}
      </span>
    </div>
  )
}

export function SubagentChildItem({
  subagent,
  isOpen,
  onOpenChange,
}: {
  subagent: SubagentState
  isOpen?: boolean
  onOpenChange?: (index: number, isOpen: boolean) => void
}) {
  const { index, status, content, thinkingContent } = subagent
  const title = content
    ? content.split('\n')[0].slice(0, 60) + (content.length > 60 ? '…' : '')
    : `Subagent ${index + 1}`
  const hasContent = !!(content || thinkingContent)

  if (hasContent) {
    return (
      <Collapsible open={isOpen ?? false} onOpenChange={(open) => onOpenChange?.(index, open)}>
        <Collapsible.Trigger
          className="flex w-full min-w-0 items-center justify-start gap-1.5 p-0 py-1.5 text-[12px] font-normal"
          showIcon={false}
        >
          <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
            <span
              className={twMerge(
                'h-1.5 w-1.5 rounded-full',
                status === 'streaming' ? 'bg-warning animate-pulse' : 'bg-success',
              )}
            />
          </span>
          <span className="text-foreground-secondary pointer-events-none min-w-0 truncate text-left text-[12px] font-normal leading-4">
            {title}
          </span>
        </Collapsible.Trigger>
        <Collapsible.Content innerClassName="mt-0 ml-[7px] gap-0">
          <div className="flex flex-col gap-2 border-l py-1 pl-3">
            {thinkingContent && (
              <div className="flex flex-col gap-1">
                <span className="text-foreground-placeholder text-[10px] uppercase tracking-wider">Reasoning</span>
                <span className="text-foreground-secondary type-prose-xs break-words">{thinkingContent}</span>
              </div>
            )}
            {content && (
              <div className="flex flex-col gap-1">
                <span className="text-foreground-placeholder text-[10px] uppercase tracking-wider">Result</span>
                <span className="text-foreground-secondary type-prose-xs break-words">{content}</span>
              </div>
            )}
          </div>
        </Collapsible.Content>
      </Collapsible>
    )
  }

  return (
    <div className="flex min-w-0 items-center gap-1.5 py-1.5">
      <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
        <span
          className={twMerge(
            'h-1.5 w-1.5 rounded-full',
            status === 'streaming' ? 'bg-warning animate-pulse' : 'bg-success',
          )}
        />
      </span>
      <span className="text-foreground-secondary min-w-0 truncate text-[12px] font-medium leading-4">
        Subagent {index + 1}
      </span>
    </div>
  )
}

export const ToolCallListItem = memo(function ToolCallListItem({
  toolCall,
  justRevealed,
  onExpand,
  disableExpand,
  isOpen,
  onOpenChange,
  conversationId,
}: {
  toolCall: ToolCall
  justRevealed?: boolean
  onExpand?: () => void
  disableExpand?: boolean
  isOpen?: boolean
  onOpenChange?: (toolId: string, isOpen: boolean) => void
  conversationId?: string
}) {
  if (toolCall.toolName === 'subagent') {
    return <SubagentListItem toolCall={toolCall} justRevealed={justRevealed} />
  }

  // Calls without a surfaced action must submit the answer as a tool result.
  // `rendersInlineAskUserCard` is the same predicate ToolCallList partitions
  // on, so a card that renders here is one it already hoisted out of the group.
  if (toolCall.toolName === BUILTIN_TOOLS.ASK_USER_QUESTION) {
    const parsedInput = parseAskUserQuestionInput(toolCall.toolInput)
    if (parsedInput && rendersInlineAskUserCard(toolCall)) {
      return (
        <div className="py-1.5">
          <AskUserQuestionCard
            input={parsedInput}
            toolCallId={toolCall.id}
            conversationId={conversationId}
            existingResult={toolCall.result}
          />
        </div>
      )
    }
  }

  if (toolCall.toolName === BUILTIN_TOOLS.ASK_USER_FEEDBACK) {
    const parsedInput = parseAskUserFeedbackInput(toolCall.toolInput)
    if (parsedInput && rendersInlineAskUserCard(toolCall)) {
      return (
        <div className="py-1.5">
          <AskUserFeedbackCard
            input={parsedInput}
            toolCallId={toolCall.id}
            conversationId={conversationId}
            existingResult={toolCall.result}
          />
        </div>
      )
    }
  }

  const view = resolveToolView(toolCall)
  const hasExpandableContent = !disableExpand && !!(view.description || toolCall.result || toolCall.toolInput)

  if (hasExpandableContent) {
    return (
      <Collapsible
        open={isOpen ?? false}
        onOpenChange={(open) => {
          onOpenChange?.(toolCall.id, open)
          if (open && onExpand) onExpand()
        }}
      >
        <Collapsible.Trigger
          className="flex w-full min-w-0 items-center justify-start gap-1.5 p-0 py-1.5 text-[12px] font-normal"
          showIcon={false}
        >
          <ToolCallIcon toolCall={toolCall} isNewlyCompleted={justRevealed} />
          <span className="text-foreground-secondary pointer-events-none min-w-0 truncate text-left text-[12px] leading-4">
            {view.name}
          </span>
        </Collapsible.Trigger>
        <Collapsible.Content innerClassName="mt-0 gap-0">
          <div className="pb-1.5 pl-1.5">
            <ToolExpandedContent toolCall={toolCall} description={view.description} />
          </div>
        </Collapsible.Content>
      </Collapsible>
    )
  }

  return (
    <div className="flex min-w-0 items-center gap-1.5 py-1.5">
      <ToolCallIcon toolCall={toolCall} isNewlyCompleted={justRevealed} />
      <span className="text-foreground-secondary min-w-0 truncate text-left text-[12px] leading-4">{view.name}</span>
    </div>
  )
})
